import { useClerk } from "@clerk/clerk-react";
import { useNavigate } from "@tanstack/react-router";
import { CircleAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { describeError } from "@/lib/errors";

// components/layout/subscription-error.tsx — rendered when the
// /me/subscription query fails for any reason other than the user
// not being signed in. The previous behaviour collapsed every error
// into "subscription inactive" and redirected the user to
// /waiting-for-activation, which is wrong: a backend outage, a
// mismatched VITE_API_KEY, or a 500 should not be reported as
// "subscription not ready".
//
// UX:
//   • Headline + description come from describeError so the
//     network/401/500 branches all surface the right copy.
//   • "Tentar novamente" re-runs the query without reloading the page.
//   • "Sair" signs the user out and routes to /signin — same escape
//     hatch as /waiting-for-activation.
export function SubscriptionError({
  error,
  refetch,
  isRetrying,
}: {
  error: Error;
  refetch: () => void;
  isRetrying?: boolean;
}) {
  const navigate = useNavigate();
  const clerk = useClerk();
  const described = describeError(error, { action: "verificar sua assinatura" });

  return (
    <div
      className="flex min-h-svh items-center justify-center px-4 py-8"
      data-testid="subscription-error"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <CircleAlert className="text-destructive mb-2 h-10 w-10" />
          <CardTitle>{described.title}</CardTitle>
          {described.description && <CardDescription>{described.description}</CardDescription>}
        </CardHeader>
        {described.hint && (
          <CardContent className="text-muted-foreground text-center text-sm">
            <p>{described.hint}</p>
          </CardContent>
        )}
        <CardContent className="flex flex-col items-center gap-2">
          <Button
            onClick={refetch}
            disabled={isRetrying}
            data-testid="subscription-error-retry"
            className="w-full"
          >
            {isRetrying ? "Tentando…" : "Tentar novamente"}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              // ponytail: sign-out errors are non-fatal — see /signout
              // for the same fire-and-forget pattern.
              void clerk.signOut().catch(() => undefined);
              navigate({ to: "/signin" });
            }}
            data-testid="subscription-error-signout"
            className="w-full"
          >
            Sair
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
